import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { EVENT_LABELS, type EventType } from '@/lib/constants';
import { cn, formatTime } from '@/lib/utils';
import { EventIcon } from './EventIcon';

interface Event {
  id: string;
  eventType: EventType;
  pageUrl?: string;
  occurredAt: string;
  eventData?: Record<string, unknown>;
}

interface TimelineEventProps {
  event: Event;
  isConversion?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
}

export function TimelineEvent({ event, isConversion, isFirst: _isFirst, isLast }: TimelineEventProps) {
  const [expanded, setExpanded] = useState(false);
  const hasEventData = event.eventData && Object.keys(event.eventData).length > 0;
  const pageUrl = event.pageUrl || null;

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div
        className={cn(
          'relative flex gap-3 group',
          isConversion && 'bg-gradient-to-r from-emerald-50 to-transparent rounded-lg p-3 -ml-3 border-l-2 border-emerald-500'
        )}
      >
        {/* Timeline line - only show if not last */}
        {!isLast && (
          <div className="absolute left-[15px] top-8 bottom-0 w-px bg-slate-200" />
        )}

        {/* Event node */}
        <div className="relative z-10 flex-shrink-0">
          <EventIcon eventType={event.eventType} />
        </div>

        {/* Event content */}
        <div className="flex-1 min-w-0 pb-4">
          <CollapsibleTrigger asChild disabled={!hasEventData}>
            <div className={cn(hasEventData && 'cursor-pointer')}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 text-left">
                  <span className={cn(
                    'font-medium text-sm text-slate-800',
                    isConversion && 'text-emerald-700'
                  )}>
                    {EVENT_LABELS[event.eventType] || event.eventType}
                  </span>
                  {isConversion && (
                    <Badge variant="success" className="text-[10px] px-1.5 py-0">
                      Converted
                    </Badge>
                  )}
                  {hasEventData && (
                    <ChevronDown
                      className={cn(
                        'h-3.5 w-3.5 text-slate-400 transition-transform duration-200',
                        expanded && 'rotate-180'
                      )}
                    />
                  )}
                </div>
                <span className="text-[11px] text-slate-400 font-mono tabular-nums flex-shrink-0">
                  {formatTime(event.occurredAt)}
                </span>
              </div>

              {pageUrl && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="text-xs text-slate-500 truncate mt-1 max-w-md">
                      {pageUrl}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-lg">
                    <p className="font-mono text-xs break-all">{pageUrl}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </CollapsibleTrigger>

          {hasEventData && (
            <CollapsibleContent className="mt-2.5">
              <div className="bg-slate-50 rounded-md border border-slate-100 overflow-hidden">
                <div className="px-3 py-1.5 bg-slate-100/50 border-b border-slate-100">
                  <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                    Event Data
                  </span>
                </div>
                <div className="p-3 space-y-1.5">
                  {Object.entries(event.eventData!).map(([key, value]) => (
                    <div key={key} className="flex gap-2 text-xs">
                      <span className="font-medium text-slate-500 min-w-[100px] flex-shrink-0">
                        {key}
                      </span>
                      <span className="text-slate-700 font-mono truncate">
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CollapsibleContent>
          )}
        </div>
      </div>
    </Collapsible>
  );
}
